import { useEffect, useState } from 'react'

import { refusePropertyRegeneration } from '../../../api/services/property'
import { useUpdateProperty } from './useUpdateProperty'
import { usePropertyRegeneration } from './usePropertyRegeneration'

export function useUpdatePropsModal({
    props,
    isReady,
    setOneProp,
}) {
    const [updateModalOpen, setUpdateModalOpen] = useState(false)
    const [hasDismissedUpdateModal, setHasDismissedUpdateModal] = useState(false)
    const [actionStatus, setActionStatus] = useState({})

    const { updatePropertySections } = useUpdateProperty()
    const { regenerateProperty: regeneratePropertyHook } = usePropertyRegeneration()

    const pendingProps = props.filter((p) => {
        const oldVersionDate = p.data?.old_version_date
        const refusedUpdateDate = p.data?.refused_update_date
        return p.checked && oldVersionDate && (!refusedUpdateDate || refusedUpdateDate < oldVersionDate)
    })
    
    // Keep acted-on props visible (updated/refused) so status messages remain,
    // even after they stop matching the "pending" filter.
    const displayProps = (() => {
        const combined = new Map()
        pendingProps.forEach((p) => combined.set(String(p.ref), p))
        Object.keys(actionStatus).forEach((ref) => {
            const prop = props.find((p) => String(p.ref) === String(ref))
            if (prop) {
                combined.set(String(prop.ref), prop)
            }
        })
        return Array.from(combined.values())
    })()

    useEffect(() => {
        if (!hasDismissedUpdateModal && isReady && pendingProps.length > 0) {
            setUpdateModalOpen(true)
        }
    }, [pendingProps.length, hasDismissedUpdateModal, isReady])

    const updateSingleProp = async (prop) => {
        const propRef = String(prop.ref)
        if (actionStatus[propRef] === 'updating' || actionStatus[propRef] === 'updated') {
            return
        }

        const sections = prop.data?.sections || []
        if (!sections.length) {
            return
        }

        setActionStatus((prev) => ({ ...prev, [propRef]: 'updating' }))
        const { data, error } = await updatePropertySections(sections)
        if (error) {
            console.error('Error updating property:', error)
            setActionStatus((prev) => ({ ...prev, [propRef]: 'error' }))
            return
        }

        const update = data.find((item) => String(item.ref) === propRef)
        if (update) {
            const updatedProp = { ...prop, data: update.new_data }
            setOneProp(updatedProp)
            setActionStatus((prev) => ({ ...prev, [propRef]: 'updated' }))
        } else {
            setActionStatus((prev) => ({ ...prev, [propRef]: 'error' }))
        }
    }

    const regenerateProperty = async (prop) => {
        const propRef = String(prop.ref)
        if (actionStatus[propRef] === 'regenerating' || actionStatus[propRef] === 'regenerated') {
            return
        }

        setActionStatus((prev) => ({ ...prev, [propRef]: 'regenerating' }))
        
        const result = await regeneratePropertyHook(prop)
        
        if (result.error) {
            console.error('Error regenerating property:', result.error)
            setActionStatus((prev) => ({ ...prev, [propRef]: 'error' }))
        } else {
            setActionStatus((prev) => ({ ...prev, [propRef]: 'regenerated' }))
        }
    }

    return {
        updateModalOpen,
        setUpdateModalOpen,
        setHasDismissedUpdateModal,
        displayProps,
        actionStatus,
        updateSingleProp,
        regenerateProperty,
        refuseSingleProp: async (prop) => {
            const propRef = String(prop.ref)
            if (actionStatus[propRef] === 'refusing' || actionStatus[propRef] === 'refused') {
                return
            }
            setActionStatus((prev) => ({ ...prev, [propRef]: 'refusing' }))
            try {
                const resp = await refusePropertyRegeneration(prop.ref)
                if (resp?.data?.status === "refused") {
                    const refusedAt = new Date().toISOString().split("T")[0]
                    const updatedProp = {
                        ...prop,
                        refused_update_date: refusedAt,
                        data: prop.data
                            ? { ...prop.data, refused_update_date: refusedAt }
                            : prop.data
                    }
                    setOneProp(updatedProp)
                    setActionStatus((prev) => ({ ...prev, [propRef]: 'refused' }))
                } else {
                    console.error("Unexpected refuse response:", resp)
                    setActionStatus((prev) => ({ ...prev, [propRef]: 'error' }))
                }
            } catch (error) {
                console.error("Failed to refuse property:", error)
                setActionStatus((prev) => ({ ...prev, [propRef]: 'error' }))
            }
        },
        getOldVersionDate: (p) => p.data?.old_version_date,
    }
}
